import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { RefreshCw, Clock, Sparkles, Image as ImageIcon, Palette, Pencil, Check, X, CheckCircle } from 'lucide-react';
import { usePermission } from '../../../../hooks/useAuth';
import { useBreadcrumbLabel } from '../../../../contexts/BreadcrumbLabelContext';
import { useVfxSchedule, useUpdateVfxSchedule } from '../../../../hooks/useApi';
import { LoadingSpinner } from '../../../ui/Loading';
import { Card, CardContent } from '../../../ui/Card';
import Button from '../../../ui/Button';
import Input from '../../../ui/Input';
import VfxConfigForm from '../VfxConfigForm';
import BackgroundConfigForm from '../BackgroundConfigForm';
import AppThemeConfigForm from '../AppThemeConfigForm';
import { PageHeader } from '@kioskgaming/ui';
import { useFormatDate } from '../../../../hooks/useFormatDate';

const toDatetimeLocal = (iso: string) => {
  try {
    return new Date(iso).toISOString().slice(0, 16);
  } catch {
    return '';
  }
};

type TabType = 'vfx' | 'background' | 'appTheme';

const ScheduleDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const canManageVfx = usePermission('vfxConfigManagement');
  const fmt = useFormatDate();
  const { data: scheduleData, isLoading, error, refetch } = useVfxSchedule(id || '');
  useBreadcrumbLabel(scheduleData?.data?.name);
  const updateSchedule = useUpdateVfxSchedule();
  const [activeTab, setActiveTab] = useState<TabType>('vfx');
  const [isEditingTime, setIsEditingTime] = useState(false);
  const [editStartTime, setEditStartTime] = useState('');
  const [editEndTime, setEditEndTime] = useState('');

  if (!canManageVfx) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <p className="text-destructive text-lg font-semibold">Access Denied</p>
          <p className="text-muted-foreground mt-2">
            You don't have permission to manage VFX schedules.
          </p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <p className="text-destructive text-lg font-semibold">Failed to load schedule</p>
          <p className="text-muted-foreground mt-2">
            {error instanceof Error ? error.message : 'An error occurred while loading the schedule.'}
          </p>
          <Button onClick={() => refetch()} className="mt-4" variant="outline">
            <RefreshCw className="h-4 w-4 mr-2" />
            Try Again
          </Button>
        </div>
      </div>
    );
  }

  if (isLoading || !scheduleData) {
    return (
      <div className="flex items-center justify-center h-96">
        <LoadingSpinner />
      </div>
    );
  }

  const schedule = scheduleData.data;

  const handleVfxSuccess = async () => {
    // This will be handled by the form component
    refetch();
  };

  const handleBackgroundSuccess = async () => {
    refetch();
  };

  const handleAppThemeSuccess = async () => {
    refetch();
  };

  const startEditTime = () => {
    setEditStartTime(toDatetimeLocal(schedule.startTime));
    setEditEndTime(toDatetimeLocal(schedule.endTime));
    setIsEditingTime(true);
  };

  const cancelEditTime = () => {
    setIsEditingTime(false);
  };

  const saveEditTime = async () => {
    if (!id || !editStartTime || !editEndTime) return;
    if (new Date(editEndTime) < new Date(editStartTime)) return;
    try {
      await updateSchedule.mutateAsync({
        id,
        data: {
          start_time: new Date(editStartTime).toISOString(),
          end_time: new Date(editEndTime).toISOString(),
        },
      });
      refetch();
      setIsEditingTime(false);
    } catch {
      // Error handled by hook (toast)
    }
  };

  const getStatusBadge = (isActive: boolean) => {
    if (isActive) {
      return (
        <span className="px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
          Active
        </span>
      );
    }
    return (
      <span className="px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
        Inactive
      </span>
    );
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <PageHeader
        back={{ onClick: () => navigate('/vfx-config'), label: 'Back' }}
        title={schedule.name}
        description="Schedule configuration details"
        actions={
          <Button onClick={() => refetch()} variant="outline" size="sm">
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
        }
      />

      {/* Schedule Info */}
      <Card>
        <CardContent className="pt-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {!isEditingTime ? (
              <div className="md:col-span-4 flex flex-wrap items-center gap-4">
                <div className="flex items-center space-x-3">
                  <div className="p-2 bg-blue-100 rounded-lg">
                    <Clock className="h-5 w-5 text-blue-600" />
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Start Time</p>
                    <p className="text-sm font-medium">
                      {fmt(schedule.startTime)}
                    </p>
                  </div>
                </div>
                <div className="flex items-center space-x-3">
                  <div className="p-2 bg-purple-100 rounded-lg">
                    <Clock className="h-5 w-5 text-purple-600" />
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">End Time</p>
                    <p className="text-sm font-medium">
                      {fmt(schedule.endTime)}
                    </p>
                  </div>
                </div>
                <div className="flex items-center space-x-3">
                  <div className="p-2 bg-green-100 rounded-lg">
                    <CheckCircle className="h-5 w-5 text-green-600" />
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Status</p>
                    <div className="mt-1">{getStatusBadge(schedule.isActive)}</div>
                  </div>
                </div>
                {schedule.updatedAt && (
                  <div className="flex items-center space-x-3">
                    <div className="p-2 bg-gray-100 rounded-lg">
                      <Clock className="h-5 w-5 text-gray-600" />
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground">Last Updated</p>
                      <p className="text-sm font-medium">
                        {fmt(schedule.updatedAt)}
                      </p>
                    </div>
                  </div>
                )}
                <div className="ml-auto">
                  <Button variant="outline" size="sm" onClick={startEditTime}>
                    <Pencil className="h-4 w-4 mr-2" />
                    Change time
                  </Button>
                </div>
              </div>
            ) : (
              <div className="md:col-span-4 space-y-4 p-4 bg-muted/30 rounded-lg">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs text-muted-foreground mb-1">Start Time</label>
                    <Input
                      type="datetime-local"
                      value={editStartTime}
                      onChange={(e) => setEditStartTime(e.target.value)}
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-muted-foreground mb-1">End Time</label>
                    <Input
                      type="datetime-local"
                      value={editEndTime}
                      onChange={(e) => setEditEndTime(e.target.value)}
                      min={editStartTime}
                    />
                  </div>
                </div>
                {editEndTime && editStartTime && new Date(editEndTime) < new Date(editStartTime) && (
                  <p className="text-xs text-destructive">End time must be after start time</p>
                )}
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    onClick={saveEditTime}
                    disabled={
                      updateSchedule.isLoading ||
                      !editStartTime ||
                      !editEndTime ||
                      new Date(editEndTime) < new Date(editStartTime)
                    }
                  >
                    {updateSchedule.isLoading ? (
                      <LoadingSpinner className="h-4 w-4" />
                    ) : (
                      <>
                        <Check className="h-4 w-4 mr-2" />
                        Save
                      </>
                    )}
                  </Button>
                  <Button variant="outline" size="sm" onClick={cancelEditTime} disabled={updateSchedule.isLoading}>
                    <X className="h-4 w-4 mr-2" />
                    Cancel
                  </Button>
                </div>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Tabs Navigation */}
      <Card>
        <CardContent className="p-0">
          <div className="border-b border-gray-200">
            <nav className="flex space-x-1" aria-label="Tabs">
              <button
                onClick={() => setActiveTab('vfx')}
                className={`
                  flex items-center space-x-2 px-6 py-4 text-sm font-medium border-b-2 transition-colors
                  ${activeTab === 'vfx'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-muted-foreground hover:text-foreground hover:border-gray-300'
                  }
                `}
              >
                <Sparkles className="h-4 w-4" />
                <span>VFX Configuration</span>
              </button>
              <button
                onClick={() => setActiveTab('background')}
                className={`
                  flex items-center space-x-2 px-6 py-4 text-sm font-medium border-b-2 transition-colors
                  ${activeTab === 'background'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-muted-foreground hover:text-foreground hover:border-gray-300'
                  }
                `}
              >
                <ImageIcon className="h-4 w-4" />
                <span>Background Configuration</span>
              </button>
              <button
                onClick={() => setActiveTab('appTheme')}
                className={`
                  flex items-center space-x-2 px-6 py-4 text-sm font-medium border-b-2 transition-colors
                  ${activeTab === 'appTheme'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-muted-foreground hover:text-foreground hover:border-gray-300'
                  }
                `}
              >
                <Palette className="h-4 w-4" />
                <span>App Theme Configuration</span>
              </button>
            </nav>
          </div>
        </CardContent>
      </Card>

      {/* Tab Content */}
      <div className="mt-6">
        {activeTab === 'vfx' && (
          <VfxConfigForm
            config={schedule.vfx}
            onSuccess={handleVfxSuccess}
            scheduleId={id}
            onScheduleUpdate={async (data) => {
              if (id) {
                await updateSchedule.mutateAsync({ id, data: { vfx: data.vfx } });
              }
            }}
            externalSaving={updateSchedule.isLoading}
          />
        )}
        {activeTab === 'background' && (
          <BackgroundConfigForm
            config={schedule.background}
            onSuccess={handleBackgroundSuccess}
            scheduleId={id}
            onScheduleUpdate={async (data) => {
              if (id) {
                await updateSchedule.mutateAsync({ id, data: { background: data.background } });
              }
            }}
            externalSaving={updateSchedule.isLoading}
          />
        )}
        {activeTab === 'appTheme' && (
          <AppThemeConfigForm
            appTheme={schedule.app_themes ? (typeof schedule.app_themes === 'string' ? parseInt(schedule.app_themes, 10) : schedule.app_themes) : null}
            onSuccess={handleAppThemeSuccess}
            scheduleId={id}
            onScheduleUpdate={async (data) => {
              if (id) {
                await updateSchedule.mutateAsync({ id, data: { app_themes: data.app_themes } });
              }
            }}
            externalSaving={updateSchedule.isLoading}
          />
        )}
      </div>
    </div>
  );
};

export default ScheduleDetail;

